/**
 * Platform token service for managing platform-wide LLM API key.
 */

import { ENV } from '../config/env'
import type { ApiResponseEnvelope } from './api'
import { authenticatedFetch } from './auth-interceptor'

const API_BASE_URL = ENV.API_BASE_URL

export interface PlatformTokenStatus {
    exists: boolean
    last4?: string
    updated_at?: string
}

export interface PlatformTokenResponse {
    ok: boolean
    last4: string
    updated_at: string
}

export interface ApiErrorResponse {
    message?: string
    error?: string
    code?: string
    translation_key?: string
    details?: Record<string, unknown>
}

export class PlatformTokenService {
    private baseURL: string

    constructor(baseURL: string = API_BASE_URL) {
        this.baseURL = baseURL
    }

    /**
     * Get platform token status (requires platform owner role).
     */
    async getTokenStatus(): Promise<PlatformTokenStatus> {
        const response = await authenticatedFetch(`${this.baseURL}/api/v1/admin/platform-token`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        })

        const responseData = (await response.json()) as
            ApiErrorResponse | ApiResponseEnvelope<PlatformTokenStatus>

        if (!response.ok) {
            const errorData = responseData as ApiErrorResponse
            throw new Error(errorData.message || 'Failed to get platform token status')
        }

        // Unwrap response envelope
        const data =
            'data' in responseData ? responseData.data : (responseData as PlatformTokenStatus)
        return data
    }

    /**
     * Get platform token status (read-only, accessible to support role and above).
     * Use this for checking if token is configured before allowing story operations.
     */
    async getTokenStatusReadOnly(): Promise<PlatformTokenStatus> {
        const response = await authenticatedFetch(
            `${this.baseURL}/api/v1/admin/platform-token/status`,
            {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )

        const responseData = (await response.json()) as
            ApiErrorResponse | ApiResponseEnvelope<PlatformTokenStatus>

        if (!response.ok) {
            const errorData = responseData as ApiErrorResponse
            throw new Error(errorData.message || 'Failed to get platform token status')
        }

        // Unwrap response envelope
        const data =
            'data' in responseData ? responseData.data : (responseData as PlatformTokenStatus)
        return data
    }

    /**
     * Set or update platform token.
     */
    async saveToken(apiKey: string): Promise<PlatformTokenResponse> {
        const response = await authenticatedFetch(`${this.baseURL}/api/v1/admin/platform-token`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ api_key: apiKey }),
        })

        const responseData = (await response.json()) as
            ApiErrorResponse | ApiResponseEnvelope<PlatformTokenResponse>

        if (!response.ok) {
            const errorData = responseData as ApiErrorResponse
            throw new Error(errorData.message || 'Failed to save platform token')
        }

        // Unwrap response envelope
        const data =
            'data' in responseData ? responseData.data : (responseData as PlatformTokenResponse)
        return data
    }

    /**
     * Update platform token (alias for saveToken for consistency).
     */
    async updateToken(apiKey: string): Promise<PlatformTokenResponse> {
        return this.saveToken(apiKey)
    }

    /**
     * Remove platform token.
     */
    async removeToken(): Promise<{ ok: boolean }> {
        const response = await authenticatedFetch(`${this.baseURL}/api/v1/admin/platform-token`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
            },
        })

        const responseData = (await response.json()) as
            ApiErrorResponse | ApiResponseEnvelope<{ ok: boolean }>

        if (!response.ok) {
            const errorData = responseData as ApiErrorResponse
            throw new Error(errorData.message || 'Failed to remove platform token')
        }

        // Unwrap response envelope
        const data = 'data' in responseData ? responseData.data : (responseData as { ok: boolean })
        return data
    }
}

/**
 * Factory function to create a new PlatformTokenService instance.
 * Use this in tests for fresh, isolated instances.
 */
export const createPlatformTokenService = (baseURL?: string): PlatformTokenService =>
    new PlatformTokenService(baseURL)

/**
 * Default singleton instance for production use.
 */
export const platformTokenService = createPlatformTokenService()
